import { HttpErrorResponse } from '@angular/common/http';

// MSAL BrowserAuthError codes for a failed network call. App code may not import
// @azure/msal-browser, so MSAL errors are matched by name and errorCode. MSAL 5 raises
// 'timed_out' only when a silent-renewal iframe (or a redirect) never answers, not for a slow
// network, so it is not here: see isInteractionRequiredError.
const TRANSIENT_MSAL_ERROR_CODES = new Set([
  'no_network_connectivity',
  'post_request_failed',
  'get_request_failed',
]);

export interface RetryOptions {
  /** The wait before each retry; the operation runs at most `delaysMs.length + 1` times. */
  delaysMs?: readonly number[];
}

/**
 * Runs `operation`, retrying it after each delay while it fails with a transient error.
 * A permanent error, or the last attempt's error, rejects the returned promise.
 */
export async function retryTransient<T>(
  operation: () => Promise<T>,
  { delaysMs = [1000, 2000] }: RetryOptions = {},
): Promise<T> {
  for (let attempt = 0; ; attempt++) {
    try {
      return await operation();
    } catch (error) {
      if (attempt >= delaysMs.length || !isTransientError(error)) {
        throw error;
      }
      await new Promise((resolve) => setTimeout(resolve, delaysMs[attempt]));
    }
  }
}

/**
 * True for failures that can succeed on a later attempt: a network failure, or 429 or 5xx from
 * HttpClient or the token endpoint. An interaction-required MSAL error is permanent: only the
 * user can resolve it.
 */
export function isTransientError(error: unknown): boolean {
  if (error instanceof HttpErrorResponse) {
    return isTransientStatus(error.status);
  }
  if (!(error instanceof Error) || !('errorCode' in error)) {
    return false;
  }
  if (error.name === 'ServerError') {
    const status = (error as { status?: number }).status;
    return status !== undefined && isTransientStatus(status);
  }
  return error.name === 'BrowserAuthError' && TRANSIENT_MSAL_ERROR_CODES.has(String(error.errorCode));
}

/**
 * True for an MSAL InteractionRequiredAuthError (interaction_required, login_required,
 * consent_required, no_tokens_found and the like): the session has expired or needs consent, and
 * only an interactive sign-in can resolve it. Also true for a BrowserAuthError 'timed_out': the
 * silent-renewal iframe never answered, and retrying it only waits another 10 s for the same.
 */
export function isInteractionRequiredError(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false;
  }
  return (
    error.name === 'InteractionRequiredAuthError' ||
    (error.name === 'BrowserAuthError' && (error as { errorCode?: unknown }).errorCode === 'timed_out')
  );
}

function isTransientStatus(status: number): boolean {
  return status === 0 || status === 429 || status >= 500;
}
